import axios, { isCancel } from 'axios'
import { network } from '@utils'
import {
  handleReturn,
  isHttpError,
  MutationFunction,
  PaginatedResponse,
  QueryFunction
} from './root'
import { AttachmentAnalysisStatus } from '@constants'

export interface IAttachmentAnalysisData {
  type: 'INVOICE' | 'RECEIPT' | 'UNKNOWN_EXPENSE' | 'NOT_EXPENSE'
}

export enum IAttachmentAnalysisValueType {
  CONTACT_NAME = 'CONTACT_NAME',
  CURRENCY_CODE = 'CURRENCY_CODE',
  INVOICE_DATE = 'INVOICE_DATE',
  INVOICE_DUE_DATE = 'INVOICE_DUE_DATE',
  RECEIPT_DATE = 'RECEIPT_DATE',
  TOTAL_AMOUNT = 'TOTAL_AMOUNT'
}

export interface IAttachmentAnalysisValue {
  type: IAttachmentAnalysisValueType
  value?: string
}

export interface IAttachmentAnalysis {
  values: IAttachmentAnalysisValue[]
  analysis_data?: IAttachmentAnalysisData
}

export interface IAttachmentAnalysisBadge {
  key: string
  label: string
  value: string
}

export interface IFileMinimal {
  id: number
  name: string
  created_at: string
  updated_at: string
  folder_id?: number
}

export enum AttachmentMalwareScanStatus {
  PENDING = 'PENDING',
  CLEAN = 'CLEAN',
  INFECTED = 'INFECTED',
  UNSCANNABLE = 'UNSCANNABLE',
  NOT_APPLICABLE = 'NOT_APPLICABLE'
}

export interface IFile {
  id: number
  name: string
  type: string
  file: string | null
  malware_scan_status?: AttachmentMalwareScanStatus
  blurhash?: string
  blurhash_w?: number
  blurhash_h?: number
  folder_id?: number
  created_at: string
  is_deletable?: boolean
  notes?: string | null
  analysis_results: IAttachmentAnalysis[]
  analysis_badges: IAttachmentAnalysisBadge[]
  analysis_status: AttachmentAnalysisStatus
}

interface FileListProps {
  slug: string
}

interface FileListForDocumentProps {
  slug: string
  documentId: number
}

interface FileInstanceProps {
  fileId: number
  slug: string
}

export const fetchFiles: QueryFunction<FileListProps, PaginatedResponse<IFile>> = async (
  { slug },
  params = null
) => {
  const url = `/v1/business/${slug}/files/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchFilesForDocument: QueryFunction<
  FileListForDocumentProps,
  PaginatedResponse<IFile>
> = async ({ slug, documentId }, params = null) => {
  const url = `/v1/business/${slug}/document/${documentId}/files/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchFile: QueryFunction<FileInstanceProps, IFile> = async (
  { fileId, slug },
  params = null
) => {
  const url = `/v1/business/${slug}/files/${fileId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const updateFile: MutationFunction<FileInstanceProps, IFile> = async (
  { fileId, slug },
  requestData
) => {
  const url = `/v1/business/${slug}/files/${fileId}/`
  const { status, data } = await network.httpClient.request({
    url,
    data: requestData,
    method: 'PATCH',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteFile: QueryFunction<FileInstanceProps, void> = async (
  { fileId, slug },
  params = null
) => {
  const url = `/v1/business/${slug}/files/${fileId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

export interface UploadFileParams {
  slug: string
  fileName: string
  folderId?: number
}

export interface UploadProgress {
  loaded: number
  total: number
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void
  signal?: AbortSignal
}

export interface UploadFileVariables extends UploadOptions {
  file: File
}

interface DirectUploadPart {
  part_number: number
  upload_url: string
}

interface UploadedPart {
  part_number: number
  etag: string
}

interface DirectMultipartUpload {
  strategy: 'direct'
  token: string
  part_size: number
  parts: DirectUploadPart[]
}

type UploadInitializeResponse =
  | { strategy: 'multipart' }
  | {
      strategy: 'direct'
      token: string
      upload_url: string
      upload_headers: Record<string, string>
    }
  | DirectMultipartUpload

export class UploadCancelledError extends Error {
  readonly name = 'UploadCancelledError'
}

export const isUploadCancelled = (error: unknown): boolean =>
  error instanceof UploadCancelledError || isCancel(error)

const MAX_ONLINE_ATTEMPTS = 3

const isRetryable = (error: unknown) => !isHttpError(error) || error.status >= 500

const waitUntilOnline = (signal?: AbortSignal) => {
  if (signal?.aborted) return Promise.reject(new UploadCancelledError())
  if (navigator.onLine) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('online', onOnline)
      signal?.removeEventListener('abort', onAbort)
    }
    const onOnline = () => {
      cleanup()
      resolve()
    }
    const onAbort = () => {
      cleanup()
      reject(new UploadCancelledError())
    }
    window.addEventListener('online', onOnline)
    signal?.addEventListener('abort', onAbort)
  })
}

// Failures while the browser is offline do not use up an attempt.
const retryWhileOnline = async <T>(
  attempt: (isRetry: boolean) => Promise<T>,
  signal?: AbortSignal,
  canRetry = isRetryable
): Promise<T> => {
  let lastError: unknown
  let isRetry = false
  let failures = 0

  while (failures < MAX_ONLINE_ATTEMPTS) {
    await waitUntilOnline(signal)
    try {
      return await attempt(isRetry)
    } catch (error) {
      if (isUploadCancelled(error) || !canRetry(error)) throw error
      lastError = error
    }
    isRetry = true
    if (navigator.onLine) failures += 1
  }

  throw lastError
}

const postUpload = async <T>(
  url: string,
  body: unknown,
  signal?: AbortSignal,
  headers?: Record<string, string>
): Promise<T> => {
  const { status, data } = await network.httpClient.post(url, body, {
    headers,
    validateStatus: () => true,
    timeout: 60000,
    signal
  })
  return handleReturn({ status, data })
}

// Plain axios: the API client would send our auth headers to S3.
const putObject = async (
  url: string,
  body: Blob,
  headers: Record<string, string> | undefined,
  onLoaded: (loaded: number) => void,
  signal?: AbortSignal
): Promise<string | undefined> => {
  const response = await axios.put(url, body, {
    headers,
    validateStatus: () => true,
    onUploadProgress: event => onLoaded(event.loaded),
    signal
  })
  handleReturn({ status: response.status, data: response.data })
  return response.headers.etag
}

const uploadParts = async (
  baseUrl: string,
  upload: DirectMultipartUpload,
  file: File,
  { onProgress, signal }: UploadOptions
): Promise<UploadedPart[]> => {
  const uploadedParts: UploadedPart[] = []

  for (const part of upload.parts) {
    const start = (part.part_number - 1) * upload.part_size
    const body = file.slice(start, Math.min(start + upload.part_size, file.size))
    let url = part.upload_url

    const etag = await retryWhileOnline(
      async isRetry => {
        if (isRetry) {
          const { parts } = await postUpload<{ parts: DirectUploadPart[] }>(
            `${baseUrl}parts/`,
            { token: upload.token, part_numbers: [part.part_number] },
            signal
          )
          url = parts[0].upload_url
        }
        return putObject(
          url,
          body,
          undefined,
          loaded => onProgress?.({ loaded: start + loaded, total: file.size }),
          signal
        )
      },
      signal,
      // An expired part URL answers 403. The retry signs a new one.
      error => isRetryable(error) || (isHttpError(error) && error.status === 403)
    )
    if (!etag) throw new Error('S3 did not return an ETag.')
    uploadedParts.push({ part_number: part.part_number, etag })
  }

  return uploadedParts
}

export const uploadFile = async (
  file: File,
  { slug, fileName, folderId }: UploadFileParams,
  options: UploadOptions = {}
): Promise<IFile> => {
  const { onProgress, signal } = options
  const baseUrl = `/v1/business/${slug}/file_upload/`
  const upload = await postUpload<UploadInitializeResponse>(
    `${baseUrl}initialize/`,
    { name: fileName, folder_id: folderId, type: file.type, size: file.size },
    signal
  )

  if (upload.strategy === 'multipart') {
    return postUpload(
      baseUrl,
      { name: fileName, folder_id: folderId, type: file.type, file },
      signal,
      { 'Content-Type': 'multipart/form-data' }
    )
  }

  onProgress?.({ loaded: 0, total: file.size })
  let parts: UploadedPart[] | undefined
  if ('parts' in upload) {
    parts = await uploadParts(baseUrl, upload, file, options)
  } else {
    await retryWhileOnline(
      () =>
        putObject(
          upload.upload_url,
          file,
          upload.upload_headers,
          loaded => onProgress?.({ loaded, total: file.size }),
          signal
        ),
      signal
    )
  }

  return retryWhileOnline(
    () => postUpload<IFile>(`${baseUrl}complete/`, { token: upload.token, parts }, signal),
    signal
  )
}

interface DownloadFileProps {
  fileId: number
  slug: string
}

export const downloadFile: QueryFunction<DownloadFileProps, Blob> = async (
  { fileId, slug },
  params = null
) => {
  const url = `/v1/business/${slug}/files/${fileId}/download/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params,
    responseType: 'blob'
  })
  return data
}
